import { motion } from 'motion/react';
import type { ReactNode } from 'react';

export interface ModalProps {
  /** Click on the backdrop / X-button closes the modal. */
  onClose: () => void;
  /** Modal body. Owns its own card layout, paddings, content. The
   *  Modal frame ONLY provides the backdrop, centring and motion. */
  children: ReactNode;
  /** `data-co-component` written onto the backdrop so a right-click
   *  there opens the right DC section. Defaults to `Modal` (the
   *  shared frame); per-modal consumers can override (e.g.
   *  `inspectName="BuybackConfirmModal"`) so their content-specific
   *  knobs stay reachable. */
  inspectName?: string;
  /** Optional `aria-labelledby` for the inner dialog. */
  ariaLabelledBy?: string;
  /** Tailwind max-width class for the centred dialog wrapper. Defaults to a
   *  compact `max-w-[420px]`; wider modals (e.g. the item picker grid) opt
   *  into a larger frame so their content fills the wrapper and stays
   *  centred instead of overflowing it. */
  widthClassName?: string;
}

/**
 * Shared modal frame. Every modal in the app uses this wrapper so a
 * partner can theme the entire modal layer from a single Design
 * Constructor panel — `Modal → backdropFill` / `Modal → backdropBlur`
 * — and the change applies everywhere.
 *
 * The card content (titles, fields, buttons, gradients, borders)
 * is intentionally NOT styled here — it belongs to the consumer in
 * `children`. Per-page uniqueness lives in the children; the frame
 * is uniform.
 *
 * Visual chrome flows through CSS vars:
 *   --co-Modal-backdropFill   tint over the page (alpha colour)
 *   --co-Modal-backdropBlur   gaussian blur on the page behind (px)
 */
export function Modal({
  onClose,
  children,
  inspectName = 'Modal',
  ariaLabelledBy,
  widthClassName = 'max-w-[420px]',
}: ModalProps) {
  return (
    <motion.div
      aria-hidden
      data-co-component={inspectName}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{
        backgroundColor: 'var(--co-Modal-backdropFill, rgba(0,0,0,0.45))',
        backdropFilter: 'blur(var(--co-Modal-backdropBlur, 20px))',
        // Safari still ships `backdrop-filter` behind the `-webkit-`
        // prefix on older versions — duplicate so iOS users get blur too.
        WebkitBackdropFilter: 'blur(var(--co-Modal-backdropBlur, 20px))',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      onClick={onClose}
    >
      {/* Scroll-safe centering. The backdrop is the scroll container; this
          wrapper is at least viewport-tall and centers the dialog, so a modal
          taller than the viewport scrolls from the top instead of clipping.
          Result: every modal is centered on both axes, at any content size. */}
      <div className="flex min-h-full items-center justify-center p-4">
      <motion.div
        role="dialog"
        aria-modal="true"
        {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
        className={'relative w-full ' + widthClassName}
        initial={{ y: 16, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 16, opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
      </div>
    </motion.div>
  );
}
